/**
 * objectLabelToggleStorage
 *
 * Persistence adapter for the canvas label toggles (ID / IP / NAME). Wraps the
 * project's scoped `storage` util so the rest of the feature doesn't have to
 * know about storage keys, malformed-JSON handling, or migration shape checks.
 *
 * Why a thin adapter and not direct storage.get / storage.set in the
 * component:
 *   1. Tests can validate default behavior + bad-shape recovery without
 *      mocking the IndexedDB-backed storage layer.
 *   2. If we ever migrate the schema (e.g. add a fourth field), the migration
 *      lives in one place.
 *   3. The toggle component stays a dumb React widget.
 */

import { storage } from '../../../map-editor-shared/lib/utils'

export interface ObjectLabelToggles {
  id: boolean
  ip: boolean
  name: boolean
}

export const DEFAULT_OBJECT_LABEL_TOGGLES: ObjectLabelToggles = {
  id: false,
  ip: false,
  name: false,
}

/**
 * Storage key, used by the scoped `storage` util which automatically prefixes
 * with `map-editor-`. Final localStorage/IndexedDB key:
 *   map-editor-objectLabelToggles
 */
export const OBJECT_LABEL_TOGGLES_STORAGE_KEY = 'objectLabelToggles'

function isValidToggles(value: unknown): value is ObjectLabelToggles {
  if (typeof value !== 'object' || value === null) return false
  const v = value as Record<string, unknown>
  return (
    typeof v.id === 'boolean' &&
    typeof v.ip === 'boolean' &&
    typeof v.name === 'boolean'
  )
}

/**
 * Returns persisted toggles or a defensive default. Never throws — if storage
 * has been corrupted or the schema changed, we silently fall back so the user
 * just sees toggles starting in the off state.
 */
export function loadObjectLabelToggles(): ObjectLabelToggles {
  try {
    const raw = storage.get<unknown>(
      OBJECT_LABEL_TOGGLES_STORAGE_KEY,
      DEFAULT_OBJECT_LABEL_TOGGLES,
    )
    if (isValidToggles(raw)) {
      // Spread so callers can mutate without aliasing the persisted reference.
      return { id: raw.id, ip: raw.ip, name: raw.name }
    }
  } catch {
    // fall through
  }
  return { ...DEFAULT_OBJECT_LABEL_TOGGLES }
}

/**
 * Persists the toggles. Returns true on success. Failures are swallowed so a
 * UI click never explodes when storage is unavailable (private browsing,
 * full disk, etc.) — labels still update in-session, persistence just no-ops.
 */
export function saveObjectLabelToggles(toggles: ObjectLabelToggles): boolean {
  try {
    return storage.set<ObjectLabelToggles>(OBJECT_LABEL_TOGGLES_STORAGE_KEY, {
      id: !!toggles.id,
      ip: !!toggles.ip,
      name: !!toggles.name,
    })
  } catch {
    return false
  }
}
